import { useContext, useEffect, useRef, useState } from 'react'
import { Image } from 'components/Image'
import { Button } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import classNames from 'classnames'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'

import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { getFeatureFlag } from '@/utils/featureFlags'
import { createExperimentObject } from 'utils/experiments'

const QuizBanner = function ({ showQuizBanner, bannerCopy, onStart, onDecline }) {
  const {
    state: { isPageLoaded },
  } = useContext(AnalyticsContext)
  const [isReady, setIsReady] = useState(false)
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [menuCondensed] = useLayoutMenuCondensed()
  const mainLayoutRef = useRef(null)

  // for fire and forget events
  const { fireAndForget } = useTriggerEvents()
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const PFQBannerExperiment = getFeatureFlag(
    FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  // Cache the main-layout ref once on mount (client-side only)
  useEffect(() => {
    mainLayoutRef.current = document.getElementById('main-layout')
  }, [])

  // Toggle the quiz banner data attribute on the main layout element via a
  // ref instead of direct style manipulation.  The actual padding offset is
  // handled by the CSS rule in global.css:
  //   #main-layout[data-quiz-banner='true'] { padding-top: calc(var(--main-layout-base-pt) + 56px); }
  useEffect(() => {
    const mainLayout = mainLayoutRef.current
    if (!mainLayout) return
    if (!showQuizBanner || !isPageLoaded) return

    // Capture the element's current base padding so the CSS custom property
    // can be used by the global rule.
    const basePadding = getComputedStyle(mainLayout).paddingTop
    mainLayout.style.setProperty('--main-layout-base-pt', basePadding)
    mainLayout.dataset.quizBanner = 'true'

    // show banner
    setIsReady(true)

    // Banner Loaded/Displaying event
    fireAndForget({
      event_type: 'app_event',
      event_action: 'app_event',
      nav_link_section: 'PFQ Banner',
      track_event: 'product_fit_quiz_banner_loaded',
      experiments: [
        createExperimentObject(
          'Product Fit Quiz Banner',
          FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
          PFQBannerExperiment,
          'conversion'
        ),
      ],
    })

    // Cleanup: remove the data attribute so CSS reverts the padding
    return () => {
      delete mainLayout.dataset.quizBanner
      mainLayout.style.removeProperty('--main-layout-base-pt')
    }
  }, [showQuizBanner, isMobile, isPageLoaded, fireAndForget, siteEnv, PFQBannerExperiment])

  return (
    <>
      {isReady && (
        <div
          className={classNames(
            'bg-blue-400 shadow-[0_6px_40px_4px_rgb(16_24_40/11%),0_9px_12px_3px_rgb(16_24_40/5%)] fixed flex -translate-x-2/4 w-full left-2/4 h-[56px]',
            // achieve.com seems to not condense with mobile viewport
            menuCondensed ? 'top-[72px] lg:top-[64px]' : 'top-[72px] lg:top-[96px]'
          )}
          style={{ overflow: 'hidden', zIndex: 1 }}
          data-testid="pfq-banner"
        >
          {/* SVG Background Pattern */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.75,
              zIndex: 1,
            }}
          >
            <Image
              src="/multi-site-assets/achieve-arrow-banner-pattern.svg"
              alt="Background pattern"
              layout="fill"
              preload={false}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </div>

          {/* Banner Content */}
          <div
            className="flex items-center justify-between lg:justify-start w-full mx-auto max-w-[1280px] lg:gap-[20px] px-[24px]"
            style={{ position: 'relative', zIndex: 2 }}
          >
            <Typography
              variant="labelButtonSm"
              color="#fff"
              component="div"
              data-testid="pfq-text-content"
              className="pr-[6px]"
            >
              {bannerCopy}
            </Typography>

            <div className="flex items-center gap-[12px]">
              <TrackingWrapper
                track={{
                  list_name: 'Tracking pfq click event',
                  nav_link_section: 'PFQ banner',
                  click_text: 'Yes',
                  event_action: 'button_click',
                }}
                onClick={onStart}
              >
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  data-testid="pfq-yes-button"
                  style={{ width: '52px', height: '32px' }}
                >
                  <Typography component="div" variant="labelButtonSm">
                    Yes
                  </Typography>
                </Button>
              </TrackingWrapper>
              <TrackingWrapper
                track={{
                  list_name: 'Tracking pfq click event',
                  nav_link_section: 'PFQ banner',
                  click_text: 'No',
                  event_action: 'button_click',
                }}
                onClick={onDecline}
              >
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  data-testid="pfq-no-button"
                  style={{ width: '52px', height: '32px' }}
                >
                  <Typography component="div" variant="labelButtonSm">
                    No
                  </Typography>
                </Button>
              </TrackingWrapper>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export { QuizBanner }
